import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

import { DialogUI } from '../Interface';
import documentService from '../../service/newDoc.service';

const hiddenAuditFields = new Set(['updatedAt', 'updatedBy', 'createdAt', 'createdBy', 'syncKey']);
const dateOnlyFieldKeys = new Set([
  'docDate',
  'issueDate',
  'dueDate',
  'deliveryPeriodStart',
  'deliveryPeriodEnd',
  'finalizedAt',
  'respDeadLine',
  'joker_date1',
  'joker_date2',
  'joker_date3',
  'joker_date4',
  'joker_date5',
  'expDate',
  'mnfDate',
  'date',
]);
const hiddenSummaryFields = new Set([
  'id',
  'docId',
  'documentId',
  'docTypeId',
  'documentTypeId',
  'lineNum',
  'visOrder',
]);

const normalizeHistoryResponse = (response) => {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) {
    return { historyRows: payload, creationInfo: null };
  }

  return {
    historyRows: Array.isArray(payload?.history) ? payload.history : [],
    creationInfo: payload
      ? {
          createdAt: payload.createdAt ?? null,
          createdBy: payload.createdBy ?? null,
          createdByUserName: payload.createdByUserName ?? null,
        }
      : null,
  };
};

const isDateValue = (value) => {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T|\s|$)/.test(value);
};

const areValuesEqual = (left, right) => {
  return String(left ?? '') === String(right ?? '');
};

const formatPrimitiveValue = (value, dateOnly = false) => {
  if (value == null || value === '') {
    return '-';
  }

  if (typeof value === 'boolean') {
    return value ? 'Igen' : 'Nem';
  }

  if (isDateValue(value)) {
    return dayjs(value).format(dateOnly ? 'YYYY.MM.DD' : 'YYYY.MM.DD HH:mm');
  }

  return String(value);
};

const formatFieldSide = (side, fieldKey) => {
  if (!side) {
    return '-';
  }

  return formatPrimitiveValue(side.label ?? side.value, dateOnlyFieldKeys.has(fieldKey));
};

const getFieldKey = (field) => {
  return field?.fieldKey ?? field?.fieldName ?? '';
};

const getFieldLabel = (field) => {
  return field?.fieldName ?? field?.fieldKey ?? '';
};

const getHistoryUser = (entry) => {
  return entry?.changedByUserName ?? entry?.user?.userName ?? entry?.userName ?? entry?.changedBy ?? '-';
};

const getHistoryDate = (entry) => {
  return entry?.changedAt ? dayjs(entry.changedAt).format('YYYY.MM.DD HH:mm') : '-';
};

const getEntryFields = (entry) => {
  return Array.isArray(entry?.fields) ? entry.fields : [];
};

const getFieldByKey = (fields, fieldKey) => {
  return fields.find((field) => getFieldKey(field) === fieldKey);
};

const isDeletionEntry = (entry) => {
  const deletedAtField = getFieldByKey(getEntryFields(entry), 'deletedAt');
  return Boolean(deletedAtField?.to?.value && !deletedAtField?.from?.value);
};

const getVisibleFieldChanges = (fields = []) => {
  return fields.filter((field) => {
    const fieldKey = getFieldKey(field);

    if (!fieldKey) {
      return false;
    }

    if (hiddenAuditFields.has(fieldKey)) {
      return false;
    }

    if (fieldKey === 'deletedAt' || fieldKey === 'deletedBy') {
      return false;
    }

    return !areValuesEqual(field?.from?.value, field?.to?.value);
  });
};

const getVisibleRootChanges = (rootChangedFields = []) => {
  return getVisibleFieldChanges(rootChangedFields);
};

const getSummaryFields = (entry) => {
  return getEntryFields(entry).filter((field) => {
    const fieldKey = getFieldKey(field);

    if (!fieldKey) {
      return false;
    }

    if (hiddenAuditFields.has(fieldKey) || hiddenSummaryFields.has(fieldKey)) {
      return false;
    }

    if (fieldKey === 'deletedAt' || fieldKey === 'deletedBy') {
      return false;
    }

    return (
      field?.to?.value != null ||
      field?.to?.label != null ||
      field?.from?.value != null ||
      field?.from?.label != null
    );
  });
};

const getEntryIdentity = (entry, type) => {
  const fields = getEntryFields(entry);

  if (type === 'lines') {
    const itemCodeField = getFieldByKey(fields, 'itemCode');
    const itemNameField = getFieldByKey(fields, 'itemName');
    const lineNumField = getFieldByKey(fields, 'lineNum');
    const lineNum = lineNumField?.to?.value ?? lineNumField?.from?.value;
    const itemCode =
      itemCodeField?.to?.label ??
      itemCodeField?.to?.value ??
      itemCodeField?.from?.label ??
      itemCodeField?.from?.value;
    const itemName =
      itemNameField?.to?.label ??
      itemNameField?.to?.value ??
      itemNameField?.from?.label ??
      itemNameField?.from?.value;

    if (itemCode && itemName && itemCode !== itemName) {
      return lineNum != null ? `Sor ${lineNum} - ${itemCode} (${itemName})` : `${itemCode} (${itemName})`;
    }

    if (itemCode || itemName) {
      return lineNum != null ? `Sor ${lineNum} - ${itemCode ?? itemName}` : itemCode ?? itemName;
    }

    return (
      lineNum != null ? `Sor ${lineNum}` : `#${entry?.id ?? '-'}`
    );
  }

  if (type === 'attachments') {
    const nameField = getFieldByKey(fields, 'name') ?? getFieldByKey(fields, 'fileName');
    return (
      nameField?.to?.label ??
      nameField?.to?.value ??
      nameField?.from?.label ??
      nameField?.from?.value ??
      `#${entry?.id ?? '-'}`
    );
  }

  return `#${entry?.id ?? '-'}`;
};

const getEntrySecondary = (entry, type) => {
  const fields = getEntryFields(entry);

  if (type === 'lines') {
    const lineNumField = getFieldByKey(fields, 'lineNum');
    const visOrderField = getFieldByKey(fields, 'visOrder');
    const quantityField = getFieldByKey(fields, 'qty');
    const unitField = getFieldByKey(fields, 'quantityUnitId') ?? getFieldByKey(fields, 'quantityUnitName');

    const fragments = [];
    const lineNum = lineNumField?.to?.value ?? lineNumField?.from?.value;
    const visOrder = visOrderField?.to?.value ?? visOrderField?.from?.value;
    const qty = quantityField?.to?.value ?? quantityField?.from?.value;
    const unit = unitField?.to?.label ?? unitField?.to?.value ?? unitField?.from?.label ?? unitField?.from?.value;

    if (lineNum != null) {
      fragments.push(`Sorszám: ${lineNum}`);
    }

    if (visOrder != null) {
      fragments.push(`Megjelenési sorrend: ${visOrder}`);
    }

    if (qty != null) {
      fragments.push(`Mennyiség: ${qty}${unit ? ` ${unit}` : ''}`);
    }

    return fragments.length ? fragments.join(' | ') : null;
  }

  return null;
};

const normalizeEntitySection = (section = {}) => {
  const added = (section?.added ?? []).filter((entry) => getSummaryFields(entry).length > 0);
  const removed = [...(section?.removed ?? [])];
  const updated = [];

  (section?.updated ?? []).forEach((entry) => {
    if (isDeletionEntry(entry)) {
      removed.push(entry);
      return;
    }

    if (getVisibleFieldChanges(getEntryFields(entry)).length > 0) {
      updated.push(entry);
    }
  });

  return { added, removed, updated };
};

const hasSectionContent = (section) => {
  return section.added.length > 0 || section.removed.length > 0 || section.updated.length > 0;
};

const DocumentHistoryDialog = ({ open, onClose, docId, docTypeId, refreshKey }) => {
  const [historyRows, setHistoryRows] = useState([]);
  const [creationInfo, setCreationInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open || !docId || !docTypeId) {
      return;
    }

    setIsLoading(true);
    documentService
      .getHistoryById(docId, docTypeId)
      .then((data) => {
        const normalizedHistory = normalizeHistoryResponse(data);
        setHistoryRows(normalizedHistory.historyRows);
        setCreationInfo(normalizedHistory.creationInfo);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [open, docId, docTypeId, refreshKey]);

  const renderChangeTable = (changes) => {
    if (!changes.length) {
      return null;
    }

    return (
      <div className="overflow-hidden rounded border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="border-b border-slate-200 px-3 py-2 text-left">Mező</th>
              <th className="border-b border-slate-200 px-3 py-2 text-left">Régi érték</th>
              <th className="border-b border-slate-200 px-3 py-2 text-left">Új érték</th>
            </tr>
          </thead>
          <tbody>
            {changes.map((field) => (
              <tr key={getFieldKey(field)}>
                <td className="border-b border-slate-100 px-3 py-2 font-medium">{getFieldLabel(field)}</td>
                <td className="border-b border-slate-100 px-3 py-2">
                  {formatFieldSide(field.from, getFieldKey(field))}
                </td>
                <td className="border-b border-slate-100 px-3 py-2">
                  {formatFieldSide(field.to, getFieldKey(field))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSummaryFields = (entry) => {
    const summaryFields = getSummaryFields(entry).slice(0, 6);

    if (!summaryFields.length) {
      return <div className="text-sm text-slate-500">Nincs megjeleníthető részlet.</div>;
    }

    return (
      <div className="grid grid-cols-12 gap-2 text-sm">
        {summaryFields.map((field) => {
          const preferredValue =
            field?.to?.value != null || field?.to?.label != null
              ? formatFieldSide(field.to, getFieldKey(field))
              : formatFieldSide(field.from, getFieldKey(field));

          return (
            <div
              key={`${entry?.id ?? 'entry'}-${getFieldKey(field)}`}
              className="col-span-12 md:col-span-6 rounded bg-slate-50 px-3 py-2"
            >
              <span className="font-medium">{getFieldLabel(field)}:</span> {preferredValue}
            </div>
          );
        })}
      </div>
    );
  };

  const renderEntityList = (entries, type, mode) => {
    if (!entries.length) {
      return null;
    }

    return (
      <div className="space-y-3">
        {entries.map((entry, index) => {
          const visibleChanges = getVisibleFieldChanges(getEntryFields(entry));
          const title = getEntryIdentity(entry, type);
          const secondary = getEntrySecondary(entry, type);

          return (
            <div key={`${type}-${mode}-${entry?.id ?? index}`} className="rounded border border-slate-200 bg-white p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-semibold">{title}</div>
                  {secondary && <div className="text-sm text-slate-500">{secondary}</div>}
                </div>
                <div
                  className={`rounded px-2 py-1 text-xs font-semibold uppercase ${
                    mode === 'added'
                      ? 'bg-green-100 text-green-800'
                      : mode === 'removed'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {mode === 'added' ? 'Új' : mode === 'removed' ? 'Törölt' : 'Módosult'}
                </div>
              </div>

              {mode === 'updated' ? renderChangeTable(visibleChanges) : renderSummaryFields(entry)}
            </div>
          );
        })}
      </div>
    );
  };

  const renderEntitySection = (title, type, section) => {
    if (!hasSectionContent(section)) {
      return null;
    }

    return (
      <div className="space-y-3 rounded border border-slate-200 bg-slate-50 p-4">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-700">{title}</h4>
        {renderEntityList(section.added, type, 'added')}
        {renderEntityList(section.removed, type, 'removed')}
        {renderEntityList(section.updated, type, 'updated')}
      </div>
    );
  };

  return (
    <DialogUI
      open={open}
      onClose={onClose}
      closeBtn
      headerContent="Módosítások áttekintése"
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '1300px',
          },
        },
      }}
    >
      <div className="mt-4 max-h-[750px] overflow-y-auto overflow-x-hidden custom-scrollbar space-y-4">
        {isLoading && <div className="py-10 text-center">Betöltés...</div>}
        {!isLoading && creationInfo && (
          <div className="grid grid-cols-12 gap-3 rounded border border-slate-300 bg-slate-50 px-4 py-3">
            <div className="col-span-12 md:col-span-6">
              <div className="text-xs font-semibold uppercase text-slate-500">Létrehozta</div>
              <div>{creationInfo.createdByUserName ?? creationInfo.createdBy ?? '-'}</div>
            </div>
            <div className="col-span-12 md:col-span-6">
              <div className="text-xs font-semibold uppercase text-slate-500">Létrehozás ideje</div>
              <div>{creationInfo.createdAt ? dayjs(creationInfo.createdAt).format('YYYY.MM.DD HH:mm') : '-'}</div>
            </div>
          </div>
        )}
        {!isLoading && !historyRows.length && <div className="py-10 text-center">Nincs elérhető előzmény.</div>}
        {!isLoading &&
          historyRows.map((row, index) => {
            const rootChanges = getVisibleRootChanges(row?.changes?.rootChangedFields ?? []);
            const attachmentChanges = normalizeEntitySection(row?.changes?.attachments);
            const lineChanges = normalizeEntitySection(row?.changes?.lines);

            const hasVisibleContent =
              rootChanges.length > 0 ||
              hasSectionContent(attachmentChanges) ||
              hasSectionContent(lineChanges);

            return (
              <div key={row?.revId ?? `${index}-${getHistoryDate(row)}`} className="rounded border border-slate-300 bg-white">
                <div className="grid grid-cols-12 gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="col-span-12 md:col-span-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">Revízió</div>
                    <div>{row?.revId ?? '-'}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">Módosította</div>
                    <div>{getHistoryUser(row)}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">Dátum</div>
                    <div>{getHistoryDate(row)}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">Következő revízió</div>
                    <div>{row?.nextRevId ?? '-'}</div>
                  </div>
                </div>

                <div className="space-y-3 p-4">
                  {!hasVisibleContent && <div className="text-sm text-slate-500">Nincs megjeleníthető változás.</div>}

                  {rootChanges.length > 0 && (
                    <div className="space-y-3 rounded border border-slate-200 bg-slate-50 p-4">
                      <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-700">Fejléc mezők</h4>
                      {renderChangeTable(rootChanges)}
                    </div>
                  )}
                  {renderEntitySection('Sorok', 'lines', lineChanges)}
                  {renderEntitySection('Csatolmányok', 'attachments', attachmentChanges)}
                </div>
              </div>
            );
          })}
      </div>
    </DialogUI>
  );
};

export default DocumentHistoryDialog;
